import { AfterViewInit, ChangeDetectionStrategy, ChangeDetectorRef, Component, ElementRef, Input, OnChanges, OnDestroy } from '@angular/core';
import { videoFrameSource, videoImage } from '../video-detail-thumb';
import { captureVideoFrame } from './video-frame-capture';

const PRELOAD_MARGIN = '300px';

/*
 * Picture of a video, filling its parent: the uploaded image, else a still frame grabbed from its
 * lightest HLS stream (only near the viewport, cached per video), else the classic generic image.
 */
@Component({
  selector: 'app-vd-thumb',
  templateUrl: './vd-thumb.component.html',
  styleUrls: ['./vd-thumb.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class VdThumbComponent implements OnChanges, AfterViewInit, OnDestroy {
  @Input() info: any;
  /** Above the fold (main poster): load at once with high priority. */
  @Input() eager = false;
  @Input() fallback = '';

  image: string | null = null;
  frame: string | null = null;
  /** JPEG data URL of the grabbed frame. */
  frameImage: string | null = null;
  visible = false;
  frameFailed = false;
  private observer?: IntersectionObserver;

  constructor(private host: ElementRef<HTMLElement>, private cdr: ChangeDetectorRef) {}

  get showFallback(): boolean {
    return !this.image && (!this.frame || this.frameFailed) && !!this.fallback;
  }

  ngOnChanges(): void {
    this.image = videoImage(this.info);
    this.frame = this.image ? null : videoFrameSource(this.info);
    this.frameImage = null;
    this.frameFailed = false;
    if (this.visible) this.loadFrame();
  }

  ngAfterViewInit(): void {
    if (this.eager || typeof IntersectionObserver === 'undefined') {
      this.show();
      return;
    }
    this.observer = new IntersectionObserver(entries => {
      if (entries.some(entry => entry.isIntersecting)) this.show();
    }, { rootMargin: PRELOAD_MARGIN });
    this.observer.observe(this.host.nativeElement);
  }

  ngOnDestroy(): void {
    this.observer?.disconnect();
  }

  private show(): void {
    this.visible = true;
    this.observer?.disconnect();
    this.loadFrame();
    this.cdr.markForCheck();
  }

  private loadFrame(): void {
    const source = this.frame;
    if (!source) return;
    captureVideoFrame(source).then(dataUrl => {
      if (source !== this.frame) return;
      this.frameImage = dataUrl;
      this.frameFailed = !dataUrl;
      this.cdr.markForCheck();
    });
  }
}
